import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { FaChevronLeft, FaChevronRight, FaExternalLinkAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Views, momentLocalizer, type View } from "react-big-calendar";
import { formatPortalLabel } from "utils/portal-access";
import { DataTableSkeleton } from "./loading";
import { EmptyState } from "./ui";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendar-schedule.css";
import {
  CalendarScheduleActionLink,
  CalendarScheduleCalendar,
  CalendarScheduleCategoryHint,
  CalendarScheduleDetailsLabel,
  CalendarScheduleStatusHint,
  CalendarScheduleSection,
  CalendarScheduleSectionSenary,
  CalendarScheduleGridLayoutTertiary,
  CalendarScheduleMediaSurface,
  CalendarScheduleInfoRow,
  CalendarScheduleSectionSecondary,
  CalendarScheduleMetaList,
  CalendarScheduleMetaRow,
  CalendarScheduleGridLayoutQuaternary,
  CalendarScheduleTextGroup,
  CalendarScheduleHeaderRow,
  CalendarScheduleSectionSeptenary,
  CalendarScheduleSectionTertiary,
  CalendarScheduleSectionQuaternary,
  CalendarScheduleGridLayout,
  CalendarScheduleFaCalendarAlt,
  CalendarScheduleFaClock,
  CalendarScheduleFaMapMarkerAlt,
  CalendarScheduleTitleText,
  CalendarScheduleEyebrowTextSecondary,
  CalendarScheduleEyebrowTextTertiary,
  CalendarScheduleTextBlockTertiary,
  CalendarScheduleBodyTextSenary,
  CalendarScheduleBodyTextSeptenary,
  CalendarScheduleTextBlock,
  CalendarScheduleTextBlockSecondary,
  CalendarScheduleInlineText,
  CalendarScheduleSidebar,
  CalendarScheduleStatCard,
  CalendarScheduleStatLabel,
  CalendarScheduleStatsStrip,
  CalendarScheduleStatValue,
  CalendarScheduleToolbarNav,
  CalendarScheduleToolbarViews,
} from "components/admin/styles/calendar-schedule.inline.styles";
const localizer = momentLocalizer(moment);
export type ScheduleCalendarEntry = {
  id: string | number;
  title: string;
  start: string | Date;
  end?: string | Date | null;
  allDay?: boolean;
  category?: "match" | "event" | "task" | string;
  location?: string;
  details?: string;
  status?: string;
  ctaHref?: string;
  ctaLabel?: string;
};
type ResolvedScheduleCalendarEntry = ScheduleCalendarEntry & {
  end: Date;
  start: Date;
};
const categoryCopy: Record<
  string,
  {
    label: string;
    tone: string;
  }
> = {
  event: {
    label: "Event",
    tone: "border-emerald-400/20 bg-emerald-500/10 text-emerald-200",
  },
  match: {
    label: "Match",
    tone: "border-[rgba(240,191,86,0.2)] bg-[rgba(240,191,86,0.12)] text-[var(--brand-highlight)]",
  },
  task: {
    label: "Task",
    tone: "border-[rgba(138,99,33,0.28)] bg-[rgba(138,99,33,0.18)] text-[#f6d69a]",
  },
};
const viewLabels: Record<string, string> = {
  [Views.AGENDA]: "Agenda",
  [Views.DAY]: "Day",
  [Views.MONTH]: "Month",
  [Views.WEEK]: "Week",
};
const normalizeScheduleDate = (value?: string | Date | null) => {
  if (!value) {
    return null;
  }
  const parsedDate = moment(value);
  return parsedDate.isValid() ? parsedDate.toDate() : null;
};
const resolveScheduleEntry = (
  entry: ScheduleCalendarEntry,
): ResolvedScheduleCalendarEntry | null => {
  const start = normalizeScheduleDate(entry.start);
  if (!start) {
    return null;
  }
  const resolvedEnd =
    normalizeScheduleDate(entry.end) ||
    (entry.allDay
      ? moment(start).endOf("day").toDate()
      : moment(start).add(90, "minutes").toDate());
  return {
    ...entry,
    end: resolvedEnd,
    start,
  };
};
const getCategoryMeta = (category?: string | null) =>
  categoryCopy[String(category || "").toLowerCase()] || {
    label: formatPortalLabel(category || "event"),
    tone: "border-white/10 bg-white/5 text-[var(--brand-muted)]",
  };
const getCategoryClassName = (category?: string | null) => {
  const normalizedCategory = String(category || "event").toLowerCase();
  if (normalizedCategory === "match") {
    return "athspire-calendar__event athspire-calendar__event--match";
  }
  if (normalizedCategory === "task") {
    return "athspire-calendar__event athspire-calendar__event--task";
  }
  return "athspire-calendar__event athspire-calendar__event--event";
};
const buildTooltip = (entry: ResolvedScheduleCalendarEntry) =>
  [entry.title, entry.location, entry.details, entry.status].filter(Boolean).join(" | ");
const formatScheduleDateTime = (value: Date) => moment(value).format("D MMM YYYY, HH:mm");
const isSparseScheduleDetails = (details?: string | null) => {
  const normalized = String(details || "").trim();
  if (!normalized) {
    return false;
  }
  return !normalized.includes("|") && normalized.length <= 28;
};

const isInternalSchedulePath = (href: string) => href.startsWith("/");

const ScheduleItemCta: React.FC<{ href: string; label: string }> = ({ href, label }) => {
  if (isInternalSchedulePath(href)) {
    return (
      <CalendarScheduleActionLink as={Link} to={href}>
        {label}
      </CalendarScheduleActionLink>
    );
  }

  return (
    <CalendarScheduleActionLink href={href} rel="noreferrer" target="_blank">
      {label}
      <FaExternalLinkAlt size={11} />
    </CalendarScheduleActionLink>
  );
};

const CalendarToolbar: React.FC<{
  date: Date;
  isCompact: boolean;
  label: string;
  onNavigate: (action: "PREV" | "TODAY" | "NEXT") => void;
  onView: (view: View) => void;
  view: View;
}> = ({ date, isCompact, label, onNavigate, onView, view }) => {
  const views = isCompact ? [Views.AGENDA, Views.DAY] : [Views.MONTH, Views.WEEK, Views.AGENDA];
  return (
    <CalendarScheduleSection>
      <CalendarScheduleSectionSecondary>
        <CalendarScheduleInlineText>Schedule</CalendarScheduleInlineText>
        <CalendarScheduleSectionTertiary>
          <CalendarScheduleTextBlock>{label}</CalendarScheduleTextBlock>
          <CalendarScheduleTextBlockSecondary>
            {moment(date).format("dddd, D MMMM YYYY")}
          </CalendarScheduleTextBlockSecondary>
        </CalendarScheduleSectionTertiary>
      </CalendarScheduleSectionSecondary>

      <CalendarScheduleSectionQuaternary>
        <CalendarScheduleToolbarNav>
          <button
            aria-label="Previous period"
            onClick={() => onNavigate("PREV")}
            type="button"
          >
            <FaChevronLeft size={12} />
          </button>
          <button data-nav="today" onClick={() => onNavigate("TODAY")} type="button">
            Today
          </button>
          <button aria-label="Next period" onClick={() => onNavigate("NEXT")} type="button">
            <FaChevronRight size={12} />
          </button>
        </CalendarScheduleToolbarNav>

        <CalendarScheduleToolbarViews>
          {views.map((availableView) => (
            <button
              className={view === availableView ? "is-active" : ""}
              key={availableView}
              onClick={() => onView(availableView)}
              type="button"
            >
              {viewLabels[availableView] || formatPortalLabel(availableView)}
            </button>
          ))}
        </CalendarScheduleToolbarViews>
      </CalendarScheduleSectionQuaternary>
    </CalendarScheduleSection>
  );
};
export const ScheduleCalendar: React.FC<{
  items: ScheduleCalendarEntry[];
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
}> = ({
  emptyDescription = "Schedule items will appear here once calendar data is available.",
  emptyTitle = "No schedule items",
  isLoading = false,
  items,
}) => {
  const calendarSurfaceRef = useRef<HTMLDivElement | null>(null);
  const [isCompact, setCompact] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 960 : false,
  );
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>(() =>
    typeof window !== "undefined" && window.innerWidth < 960 ? Views.AGENDA : Views.MONTH,
  );
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  useEffect(() => {
    const handleResize = () => {
      setCompact(window.innerWidth < 960);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    if (isCompact && ![Views.AGENDA, Views.DAY].includes(currentView)) {
      setCurrentView(Views.AGENDA);
    }
    if (!isCompact && currentView === Views.DAY) {
      setCurrentView(Views.MONTH);
    }
  }, [currentView, isCompact]);
  const resolvedItems = useMemo(
    () =>
      items
        .map(resolveScheduleEntry)
        .filter(Boolean)
        .sort(
          (left, right) => left!.start.getTime() - right!.start.getTime(),
        ) as ResolvedScheduleCalendarEntry[],
    [items],
  );
  const upcomingItems = useMemo(() => {
    const now = new Date();
    const futureEntries = resolvedItems.filter((item) => item.end.getTime() >= now.getTime());
    return (futureEntries.length ? futureEntries : resolvedItems).slice(0, 6);
  }, [resolvedItems]);
  useEffect(() => {
    if (!resolvedItems.length) {
      setSelectedId(null);
      return;
    }
    if (!resolvedItems.some((item) => String(item.id) === String(selectedId))) {
      setSelectedId(upcomingItems[0]?.id ?? resolvedItems[0]?.id ?? null);
    }
  }, [resolvedItems, selectedId, upcomingItems]);
  const selectedItem =
    resolvedItems.find((item) => String(item.id) === String(selectedId)) ||
    upcomingItems[0] ||
    resolvedItems[0] ||
    null;
  const focusScheduleItem = (item: ResolvedScheduleCalendarEntry, focusCalendarView = false) => {
    setSelectedId(item.id);
    setCurrentDate(item.start);
    if (focusCalendarView) {
      setCurrentView(isCompact ? Views.DAY : Views.WEEK);
      calendarSurfaceRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  const itemCounts = useMemo(
    () => ({
      events: resolvedItems.filter((item) => String(item.category || "").toLowerCase() === "event")
        .length,
      matches: resolvedItems.filter((item) => String(item.category || "").toLowerCase() === "match")
        .length,
      tasks: resolvedItems.filter((item) => String(item.category || "").toLowerCase() === "task")
        .length,
      thisMonth: resolvedItems.filter((item) => moment(item.start).isSame(currentDate, "month"))
        .length,
    }),
    [currentDate, resolvedItems],
  );
  if (isLoading) {
    return <DataTableSkeleton columns={4} rows={5} />;
  }
  if (!resolvedItems.length) {
    return <EmptyState description={emptyDescription} title={emptyTitle} />;
  }
  return (
    <CalendarScheduleGridLayout>
      <CalendarScheduleStatsStrip>
        <CalendarScheduleStatCard>
          <CalendarScheduleStatLabel>Total items</CalendarScheduleStatLabel>
          <CalendarScheduleStatValue>{resolvedItems.length}</CalendarScheduleStatValue>
        </CalendarScheduleStatCard>
        <CalendarScheduleStatCard>
          <CalendarScheduleStatLabel>This month</CalendarScheduleStatLabel>
          <CalendarScheduleStatValue>{itemCounts.thisMonth}</CalendarScheduleStatValue>
        </CalendarScheduleStatCard>
        <CalendarScheduleStatCard>
          <CalendarScheduleStatLabel>Fixtures</CalendarScheduleStatLabel>
          <CalendarScheduleStatValue>{itemCounts.matches}</CalendarScheduleStatValue>
        </CalendarScheduleStatCard>
        <CalendarScheduleStatCard>
          <CalendarScheduleStatLabel>Tasks & events</CalendarScheduleStatLabel>
          <CalendarScheduleStatValue>
            {itemCounts.tasks + itemCounts.events}
          </CalendarScheduleStatValue>
        </CalendarScheduleStatCard>
      </CalendarScheduleStatsStrip>

      <CalendarScheduleGridLayoutTertiary>
        <CalendarScheduleMediaSurface ref={calendarSurfaceRef}>
          <CalendarScheduleCalendar
            components={{
              toolbar: (toolbarProps: any) => (
                <CalendarToolbar
                  date={toolbarProps.date}
                  isCompact={isCompact}
                  label={toolbarProps.label}
                  onNavigate={toolbarProps.onNavigate}
                  onView={toolbarProps.onView}
                  view={toolbarProps.view}
                />
              ),
            }}
            date={currentDate}
            endAccessor="end"
            eventPropGetter={(event) => ({
              className: getCategoryClassName(event.category),
            })}
            events={resolvedItems}
            localizer={localizer}
            messages={{
              showMore: (total) => `+${total} more`,
            }}
            onNavigate={(nextDate) => setCurrentDate(nextDate)}
            onSelectEvent={(event) => focusScheduleItem(event)}
            onView={(nextView) => setCurrentView(nextView)}
            popup
            startAccessor="start"
            tooltipAccessor={(event) => buildTooltip(event)}
            view={currentView}
            views={isCompact ? [Views.AGENDA, Views.DAY] : [Views.MONTH, Views.WEEK, Views.AGENDA]}
          />
        </CalendarScheduleMediaSurface>

        <CalendarScheduleSidebar>
          {selectedItem ? (
            <CalendarScheduleSectionSenary>
              <CalendarScheduleEyebrowTextSecondary>Selected</CalendarScheduleEyebrowTextSecondary>
              <CalendarScheduleTitleText>{selectedItem.title}</CalendarScheduleTitleText>

              <CalendarScheduleInfoRow>
                <CalendarScheduleCategoryHint>
                  {getCategoryMeta(selectedItem.category).label}
                </CalendarScheduleCategoryHint>
                {isSparseScheduleDetails(selectedItem.details) ? (
                  <>
                    <CalendarScheduleCategoryHint aria-hidden>·</CalendarScheduleCategoryHint>
                    <CalendarScheduleStatusHint>
                      {formatPortalLabel(selectedItem.details)}
                    </CalendarScheduleStatusHint>
                  </>
                ) : null}
                {selectedItem.status ? (
                  <>
                    <CalendarScheduleCategoryHint aria-hidden>·</CalendarScheduleCategoryHint>
                    <CalendarScheduleStatusHint>
                      {formatPortalLabel(selectedItem.status)}
                    </CalendarScheduleStatusHint>
                  </>
                ) : null}
              </CalendarScheduleInfoRow>

              <CalendarScheduleMetaList>
                <CalendarScheduleMetaRow>
                  <CalendarScheduleFaClock size={13} />
                  <span>{formatScheduleDateTime(selectedItem.start)}</span>
                </CalendarScheduleMetaRow>
                {selectedItem.location ? (
                  <CalendarScheduleMetaRow>
                    <CalendarScheduleFaMapMarkerAlt size={13} />
                    <span className="break-words">{selectedItem.location}</span>
                  </CalendarScheduleMetaRow>
                ) : null}
              </CalendarScheduleMetaList>

              {selectedItem.details && !isSparseScheduleDetails(selectedItem.details) ? (
                <div className="mt-4 space-y-1.5 border-t border-[var(--brand-border)] pt-3">
                  <CalendarScheduleDetailsLabel>Details</CalendarScheduleDetailsLabel>
                  <p className="text-sm leading-6 text-[var(--brand-ink)]">{selectedItem.details}</p>
                </div>
              ) : null}

              {selectedItem.ctaHref ? (
                <ScheduleItemCta
                  href={selectedItem.ctaHref}
                  label={selectedItem.ctaLabel || "Open item"}
                />
              ) : null}
            </CalendarScheduleSectionSenary>
          ) : null}

          <CalendarScheduleSectionSenary>
            <CalendarScheduleHeaderRow>
              <CalendarScheduleEyebrowTextTertiary>Upcoming</CalendarScheduleEyebrowTextTertiary>
              <CalendarScheduleFaCalendarAlt size={16} />
            </CalendarScheduleHeaderRow>

            <CalendarScheduleGridLayoutQuaternary>
              {upcomingItems.map((item) => {
                const isSelected = String(item.id) === String(selectedItem?.id);
                return (
                  <button
                    className={`group rounded-[22px] border px-4 py-4 text-left transition duration-200 ${
                      isSelected
                        ? "border-transparent bg-[rgba(120,167,222,0.08)]"
                        : "border-transparent bg-[rgba(255,255,255,0.04)] hover:-translate-y-0.5 hover:bg-[rgba(120,167,222,0.06)]"
                    }`}
                    key={String(item.id)}
                    onClick={() => focusScheduleItem(item, true)}
                    type="button"
                  >
                    <CalendarScheduleSectionSeptenary>
                      <CalendarScheduleTextGroup>
                        <CalendarScheduleTextBlockTertiary>
                          {item.title}
                        </CalendarScheduleTextBlockTertiary>
                        <CalendarScheduleBodyTextSenary>
                          {formatScheduleDateTime(item.start)}
                        </CalendarScheduleBodyTextSenary>
                      </CalendarScheduleTextGroup>
                      <span
                        className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${
                          getCategoryMeta(item.category).tone
                        }`}
                      >
                        {getCategoryMeta(item.category).label}
                      </span>
                    </CalendarScheduleSectionSeptenary>
                    {item.location ? (
                      <CalendarScheduleBodyTextSeptenary>
                        {item.location}
                      </CalendarScheduleBodyTextSeptenary>
                    ) : null}
                  </button>
                );
              })}
            </CalendarScheduleGridLayoutQuaternary>
          </CalendarScheduleSectionSenary>
        </CalendarScheduleSidebar>
      </CalendarScheduleGridLayoutTertiary>
    </CalendarScheduleGridLayout>
  );
};
